import { useEffect, useState, type ReactNode } from "react";
import {
  ArrowRight,
  NotebookPen,
  Store,
  UserRound,
  Plus,
  Trash2,
} from "lucide-react";
import { api, type AvatarSummary, type Me } from "./api.ts";
import { AvatarDetail } from "./AvatarDetail.tsx";
import { NewAvatar } from "./Account.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.tsx";
import { Alert, AlertDescription } from "@/components/ui/alert.tsx";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog.tsx";

export type TenantSurface =
  | "settings"
  | "marketplace"
  | "notes"
  | "tasks"
  | "calendar"
  | "work"
  | "conversation";
export function TenantSettings({
  me,
  activeId,
  onSelect,
  onOpen,
  onChanged,
  onSignOut,
  children,
}: {
  me: Me;
  activeId: string | null;
  onSelect: (id: string) => void;
  onOpen: (surface: TenantSurface) => void;
  onChanged: () => void;
  onSignOut: () => void;
  children?: ReactNode;
}) {
  const [portal,setPortal]=useState<string|null>(null);
  useEffect(()=>{void api<{accountPortalUrl:string|null}>("/api/auth/options").then(r=>setPortal(r.accountPortalUrl)).catch(()=>{});},[]);
  const [details, setDetails] = useState<AvatarSummary | null>(null);
  const [creating, setCreating] = useState(false);
  const [removing, setRemoving] = useState<AvatarSummary | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const consultants = me.avatars.filter(
    (a) =>
      a.avatarKind === "consultant" ||
      (a.mandatory && a.avatarKind !== "representative"),
  );
  const employees = me.avatars.filter(
    (a) =>
      a.avatarKind === "employee" ||
      (!a.mandatory && a.avatarKind !== "representative"),
  );
  const representative = me.avatars.find(
    (a) => a.avatarKind === "representative" || a.id === me.defaultAvatarId,
  );
  const remove = async () => {
    if (!removing || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/api/avatars/${encodeURIComponent(removing.id)}`, {
        method: "DELETE",
      });
      setRemoving(null);
      onChanged();
    } catch {
      setError(
        "Could not remove this employee. Refresh the employee list before trying again.",
      );
    } finally {
      setBusy(false);
    }
  };
  const people = (list: AvatarSummary[], managed: boolean) => (
    <div className="grid gap-3 sm:grid-cols-2">
      {list.map((a) => (
        <Card key={a.id}>
          <CardContent className="flex flex-col gap-3 pt-4">
            <div>
              <p className="font-medium">{a.name}</p>
              <p className="text-sm text-muted-foreground">{a.role}</p>
            </div>
            <p className="text-sm text-muted-foreground line-clamp-3">
              {a.responsibility}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onSelect(a.id)}
              >
                {a.id === activeId
                  ? "Return to conversation"
                  : `Talk to ${a.name}`}
                <ArrowRight />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setDetails(a)}>
                Details
              </Button>
              {!managed && (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Delete ${a.name}`}
                  onClick={() => setRemoving(a)}
                >
                  <Trash2 />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-medium">{me.tenant.name}</p>
          <p className="text-sm text-muted-foreground">{me.user.email}</p>
        </div>
        <Badge variant="outline">
          {me.tenant.workspaceType === "personal"
            ? "Personal workspace"
            : "Business workspace"}
        </Badge>
      </div>
      {representative && (
        <Card>
          <CardHeader>
            <CardTitle>Your Delegate representative</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Your starting point for finding the right employee, keeping notes,
              and learning how Delegate works.
            </p>
            <Button
              variant="outline"
              onClick={() => onSelect(representative.id)}
            >
              Talk to Destiny <ArrowRight />
            </Button>
          </CardContent>
        </Card>
      )}
      <div className="flex flex-wrap gap-3">
        <Button variant="outline" onClick={() => onOpen("marketplace")}>
          <Store /> Employee marketplace
        </Button>
        <Button
          variant="outline"
          onClick={() => onOpen("notes")}
          disabled={!representative}
        >
          <NotebookPen /> My notes
        </Button>
        <Button variant="outline" onClick={() => onOpen("work")}>
          <UserRound /> Saved employee work
        </Button>
        <Button variant="outline" onClick={() => onOpen("conversation")}>
          Transcript
        </Button>
      </div>
      <section className="space-y-3">
        <div>
          <h3 className="font-heading text-lg font-medium">
            Delegate consultants
          </h3>
          <p className="text-sm text-muted-foreground">
            Delegate-managed points of contact. Their current capabilities are
            shown in Details.
          </p>
        </div>
        {people(consultants, true)}
        {!consultants.length && (
          <p className="text-sm text-muted-foreground">
            Consultants are being prepared for this workspace.
          </p>
        )}
      </section>
      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-heading text-lg font-medium">Your employees</h3>
          <Button variant="ghost" size="sm" onClick={() => setCreating(true)}>
            <Plus /> Custom employee
          </Button>
        </div>
        {people(employees, false)}
        {!employees.length && (
          <p className="text-sm text-muted-foreground">
            No employees hired yet. Ask Destiny to help you find one.
          </p>
        )}
      </section>
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {children}
      <div className="border-t border-border pt-4">
        {portal && <Button variant="outline" onClick={()=>location.assign(portal)}>Switch workspace</Button>}
        <Button variant="ghost" onClick={onSignOut}>
          Sign out
        </Button>
      </div>
      {details && (
        <AvatarDetail avatar={details} onClose={() => setDetails(null)} />
      )}
      {creating && (
        <NewAvatar
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            onChanged();
          }}
        />
      )}
      <AlertDialog
        open={!!removing}
        onOpenChange={(open) => {
          if (!open && !busy) setRemoving(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {removing?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the employee's tasks, topics, and
              conversations.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {error && <p role="alert">{error}</p>}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Keep employee</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={busy}
              onClick={(e) => {
                e.preventDefault();
                void remove();
              }}
            >
              {busy ? "Deleting..." : "Delete employee"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
